import { MusePeer } from '@/services/RoomContextMusePeer'
import { Button } from '@/UIComponents2/Button'
import { cn } from '@/utlis/cn'
import { Gravatar } from '@/components/Gravatar'
import { useUserContext } from '@/UserContext'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useRoomContext } from '@/RoomContext'
import { MidiControls } from './MidiControls'
import { AudioDevicePanelMe } from './AudioDevicePanelMe'
import { AudioDevicePanelPeer } from './AudioDevicePanelPeer'
import { SidebarCamera } from './SidebarCamera'
import ActiveSpeakerIndicator from '../../shared/ActiveSpeakerIndicator'
import { getPinInfo, useViewMode, ViewMode } from '../../shared/useViewMode'
import { SidebarScreen } from './SidebarScreen'
import { useCollapsibleGroupItem } from '@/UIComponents2/useCollapsibleGroup'
import { Collapsible } from '@/UIComponents2/Collapsible'
import { useNonIdempotentEffect } from '@/hooks/useNonIdempotentEffect'
import MixerIcon from '@/CustomIcons/MixerIcon'
import { SidebarTooltip } from '../SidebarTooltip'
import { OverlayControlButton } from '@/components/OverlayControlButton'
import { Username } from '@/components/Username'
import { VolumeMeter } from '@/components/VolumeMeter'
import MicrophoneOffIcon from '@/CustomIcons/MicrophoneOffIcon'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import useRoomActions from '@/services/useRoomActions'

export const cardXPad = 'px-[10px]'

type VirtualizationProps =
    | {
          isVirtualized: true
          virtualizedHeight: number
      }
    | {
          isVirtualized: false
          onHeightChanged: (height: number) => void
      }

type UserCardProps = {
    user: MusePeer | 'me'
    showCamera: boolean
} & VirtualizationProps

export function UserCard(props: UserCardProps) {
    const [userState] = useUserContext()
    const userSessionId = props.user === 'me' ? userState.sessionId : props.user.sessionId
    const [controlsOpen, setControlsOpen] = useCollapsibleGroupItem('sidebar-user-controls', userSessionId)
    const viewMode = useViewMode()

    const numInputs = useMemo(() => {
        if (props.user === 'me') {
            return userState.mediaStreams.audioInputs.reduce((n, track) => n + track.info.channelSelection.length, 0)
        }
        return props.user.remoteAudios.reduce((n, track) => n + (track.info.channelSelection?.length ?? 1), 0)
    }, [props.user, userState.mediaStreams.audioInputs])

    const renderCamera = useMemo(() => {
        if (props.user === 'me') {
            return props.showCamera
        }
        const pinInfo = getPinInfo(viewMode)
        const isCamPinned =
            pinInfo &&
            pinInfo.peerSessionId === props.user.sessionId &&
            pinInfo.kind === 'camera' &&
            !viewMode.galleryHidden
        return props.showCamera && !!props.user.remoteVideo && !isCamPinned
    }, [props.user, viewMode, props.showCamera])

    const renderScreen = useMemo(() => {
        if (props.user === 'me') {
            return false
        }
        const pinInfo = getPinInfo(viewMode)
        const isScreenPinned =
            pinInfo &&
            pinInfo.peerSessionId === props.user.sessionId &&
            pinInfo.kind === 'screen' &&
            !viewMode.galleryHidden
        const r = props.showCamera && !!props.user.remoteDesktop && !isScreenPinned
        return r
    }, [props.showCamera, props.user, viewMode])

    const renderVideos = (renderCamera || renderScreen) && !viewMode.sidebarVideosHidden

    // used for virtualization
    const tRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const divRef = useRef<HTMLDivElement>(null)
    const hasComponentLoaded = useRef(false)
    useNonIdempotentEffect(() => {
        console.log(
            `SidebarVirtualization: (username=${props.user === 'me' ? 'me' : props.user.username}) card loaded.`
        )
        hasComponentLoaded.current = true

        if (!props.isVirtualized && divRef.current) {
            // Needs timeout for some events because of animation, e.g. drawer open/close. If
            // we measure right away, then the height we get is that before the drawer opened/closed
            // fully.
            clearTimeout(tRef.current)
            tRef.current = setTimeout(() => {
                const username = props.user === 'me' ? 'me' : props.user.username

                const height = measureRef(document, viewMode, divRef.current, username)
                if (height) {
                    props.onHeightChanged && props.onHeightChanged(height)
                }
            }, 100)
        }

        return () => {
            console.log(
                `SidebarVirtualization: (username=${props.user === 'me' ? 'me' : props.user.username}) card unloading.`
            )
            hasComponentLoaded.current = false
        }
    }, [divRef.current, props.isVirtualized, renderVideos, numInputs, renderCamera, renderScreen, controlsOpen])

    const virtualizedHeightStyle = props.isVirtualized
        ? { height: props.virtualizedHeight, minHeight: props.virtualizedHeight }
        : undefined

    return (
        <div
            ref={divRef}
            className="relative w-full flex flex-col bg-[rgba(255,255,255,0.08)] shadow-glow border-muse-toplit-low rounded-[16px]"
            style={virtualizedHeightStyle}
        >
            {!props.isVirtualized && (
                <>
                    {renderVideos ? (
                        <div className="relative group">
                            {renderCamera && (
                                <SidebarCamera
                                    user={props.user}
                                    videoClassName={cn(
                                        'transition-border duration-300',
                                        !controlsOpen && !renderScreen && 'rounded-b-[16px]'
                                    )}
                                />
                            )}
                            {renderScreen && (
                                <SidebarScreen
                                    user={props.user as MusePeer}
                                    videoClassName={cn(
                                        !renderCamera && 'rounded-t-[16px]',
                                        !controlsOpen && 'rounded-b-[16px]'
                                    )}
                                />
                            )}
                            <MixerToggleOverlay open={controlsOpen} onChange={setControlsOpen} />
                        </div>
                    ) : (
                        <NameTag
                            user={props.user}
                            controlsOpen={controlsOpen}
                            setControlsOpen={setControlsOpen}
                            numInputs={numInputs}
                            userSessionId={userSessionId}
                        />
                    )}
                    <UserControls
                        user={props.user}
                        open={controlsOpen}
                        userSessionId={userSessionId}
                        showCamera={renderCamera}
                    />

                    {props.showCamera && (
                        <ActiveSpeakerIndicator participantSessionId={userSessionId} className="rounded-[16px]" />
                    )}
                </>
            )}
        </div>
    )
}

function MixerToggleOverlay(props: { open: boolean; onChange: (open: boolean) => void }) {
    const { t } = useTranslation()

    return (
        <div className="absolute bottom-1 right-1 z-10 opacity-0 group-hover:opacity-100">
            <SidebarTooltip text={props.open ? t('Hide Audio Controls') : t('Show Audio Controls')}>
                <div>
                    <OverlayControlButton onClick={() => props.onChange(!props.open)}>
                        <MixerIcon active={props.open} style={{ fontSize: '1.3rem' }} />
                    </OverlayControlButton>
                </div>
            </SidebarTooltip>
        </div>
    )
}

type NameTagProps = {
    user: MusePeer | 'me'
    numInputs: number
    controlsOpen: boolean
    setControlsOpen: (open: boolean) => void
    userSessionId: string
}
function NameTag(props: NameTagProps) {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const [roomState] = useRoomContext()

    const {
        calcIsMyAudioDeviceAllMuted,
        toggleMyAudioDeviceMute,
        toggleRemoteAudioMute,
        calcIsRemoteAudioDeviceAllMuted,
    } = useRoomActions()

    const mutedAudio = useMemo(() => {
        if (props.user === 'me') {
            return calcIsMyAudioDeviceAllMuted()
        } else {
            return calcIsRemoteAudioDeviceAllMuted(props.user)
        }
    }, [props.user, userState.mediaStreams.audioInputs, roomState.myAudioMuteState])

    const handleMuteAudio = () => {
        if (props.user === 'me') {
            toggleMyAudioDeviceMute()
        } else {
            toggleRemoteAudioMute(props.user)
        }
    }

    const gravatarEmail = props.user === 'me' ? userState.userUrn : props.user.userUrn

    const audioStreams = useMemo(() => {
        if (props.user === 'me') {
            return userState.mediaStreams.audioInputs
        }

        return props.user.remoteAudios.map((r) => r.track)
    }, [props.user])

    return (
        <Button
            className={cn(
                'w-full flex py-2 t-base-medium justify-between items-center',
                cardXPad,
                'pr-0',
                props.controlsOpen && ' border-b-1 border-white/3'
            )}
            onClick={() => props.setControlsOpen(!props.controlsOpen)}
        >
            <div className="flex gap-2 items-center">
                <Gravatar email={gravatarEmail} className="rounded-[12px]" />{' '}
                <Username isMe={props.user === 'me'} peer={props.user !== 'me' ? props.user : undefined} suffix />
            </div>
            <div className="flex items-center gap-2">
                {!props.controlsOpen && (
                    <Button
                        variant="tertiary"
                        minimal
                        className={cn(
                            'flex gap-[6px] items-center h-[30px] rounded-[12px] w-auto px-3',
                            mutedAudio ? 'text-muse-danger' : 'text-muse-fg'
                        )}
                        onClick={(ev) => {
                            ev.stopPropagation()
                            handleMuteAudio()
                        }}
                    >
                        <VolumeMeter stream={audioStreams} variant="pills" height={24} channelCount={1} />
                        {mutedAudio ? (
                            <MicrophoneOffIcon style={{ fontSize: '1.2rem' }} />
                        ) : (
                            <MicrophoneIcon style={{ fontSize: '1.2rem' }} />
                        )}
                    </Button>
                )}

                <div className="w-[30px]">
                    <SidebarTooltip text={props.controlsOpen ? t('Hide Audio Controls') : t('Show Audio Controls')}>
                        <div className="flex">
                            <MixerIcon
                                active={props.controlsOpen}
                                className="opacity-65"
                                style={{ fontSize: '1.36rem' }}
                            />
                        </div>
                    </SidebarTooltip>
                </div>
            </div>
        </Button>
    )
}

type UserControlsProps = {
    user: MusePeer | 'me'
    showCamera: boolean
    open: boolean
    userSessionId: string
}
function UserControls(props: UserControlsProps) {
    const [userState] = useUserContext()
    const [roomState] = useRoomContext()

    const midiDevice = useMemo(() => {
        if (props.user === 'me') {
            return userState.mediaSources.midiInputs.filter(
                (device) => device.id === userState.mediaSelection.midiInput
            )[0]
        } else {
            return roomState.expectedNetworkTopology === 'p2p'
                ? props.user.manifest.localP2pMidi[0]
                : props.user.manifest.localSfuMidi[0]
        }
    }, [
        userState.mediaSelection.midiInput,
        userState.mediaSources.midiInputs,
        roomState.expectedNetworkTopology,
        props.user,
    ])

    return (
        <Collapsible open={props.open}>
            <div className="pb-2 pt-3">
                {midiDevice && (
                    <div className={cn(cardXPad, 'pb-2 border-b-1 border-white/3 mb-2')}>
                        <MidiControls user={props.user} device={midiDevice as any} />
                    </div>
                )}

                {props.user === 'me' ? <AudioDevicePanelMe /> : <AudioDevicePanelPeer peer={props.user} />}
            </div>
        </Collapsible>
    )
}

function getScrollContainerWidth(document: Document, viewMode: ViewMode) {
    // NOTE: When the sidebar is popped out, the scroll container is in
    // `viewMode.sidebarPoppedOutDocument`. If the div is not in either places,
    // then it is in some transition state.

    const width1 = document.getElementById('user-list-scroll-container')?.clientWidth
    if (width1) {
        return width1
    }

    if (viewMode.sidebarPoppedOutDocument) {
        const width2 = viewMode.sidebarPoppedOutDocument.getElementById('user-list-scroll-container')?.clientWidth
        if (width2) {
            return width2
        }
    }

    return null
}

const measureRef = (document: Document, viewMode: ViewMode, node: HTMLDivElement | null, username: string) => {
    if (node == null) {
        return null
    }

    const width = node.clientWidth
    const height = node.clientHeight

    const scrollWidth = getScrollContainerWidth(document, viewMode)
    if (!scrollWidth) {
        return
    }

    // 650 is the max side bar width; width greater than this indicates the app
    // is in some transition state and the measurements are not valid.
    if (scrollWidth >= 650) {
        console.log(
            `Virtualization: measureRef: (username=${username}) SKIPPING: w=${width} h=${height} ` +
                `sidebarWidth=${scrollWidth}`
        )
        return
    }

    console.log(
        `Virtualization: measureRef: (username=${username}) saving: w=${width} h=${height} ` +
            `scrollWidth=${scrollWidth}`
    )

    return height
}
